import { Component, type ReactNode } from "react";
import * as BrowserSentry from "@sentry/react";
import * as ElectronRendererSentry from "@sentry/electron/renderer";
import {
  buildSentryRelease,
  COMMON_SENTRY_IGNORE_ERRORS,
  readFirstEnvValue,
  resolveSentryConfig,
  scrubSentryEvent,
} from "@cmmd-forge/shared/sentry";

import { APP_VERSION } from "./branding";
import { isElectron } from "./env";
import { AppErrorBoundaryFallback } from "./components/AppErrorBoundaryFallback";
import { isTransportConnectionErrorMessage } from "./rpc/transportError";

type WebSentryModule = typeof BrowserSentry | typeof ElectronRendererSentry;

const WEB_APP_NAME = "forge-web";

const WEB_SENTRY_ENV_KEYS = ["VITE_SENTRY_ENVIRONMENT", "MODE"] as const;
const WEB_SENTRY_RELEASE_KEYS = ["VITE_SENTRY_RELEASE"] as const;
const WEB_SENTRY_DSN_KEYS = ["VITE_SENTRY_DSN"] as const;
const WEB_SENTRY_DEBUG_KEYS = ["VITE_SENTRY_DEBUG"] as const;
const WEB_SENTRY_ENABLED_KEYS = ["VITE_SENTRY_ENABLED"] as const;
const WEB_SENTRY_TRACES_SAMPLE_RATE_KEYS = ["VITE_SENTRY_TRACES_SAMPLE_RATE"] as const;
const WEB_SENTRY_PROFILES_SAMPLE_RATE_KEYS = ["VITE_SENTRY_PROFILES_SAMPLE_RATE"] as const;
const WEB_SENTRY_REPLAYS_SESSION_SAMPLE_RATE_KEYS = [
  "VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE",
] as const;
const WEB_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE_KEYS = [
  "VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE",
] as const;
const WEB_SENTRY_COMMIT_KEYS = ["VITE_SENTRY_COMMIT_SHA", "VITE_COMMIT_SHA"] as const;

let initialized = false;
let feedbackIntegration: FeedbackTriggerIntegration | null = null;
let feedbackDialog: FeedbackDialog | null = null;
let feedbackDialogAttached = false;

interface FeedbackTriggerIntegration {
  readonly attachTo?: (
    element: Element | string,
    options?: Record<string, unknown>,
  ) => (() => void) | undefined;
  readonly createForm?: (options?: Record<string, unknown>) => Promise<FeedbackDialog>;
}

interface FeedbackDialog {
  readonly appendToDom: () => void;
  readonly open: () => void;
}

type WebSentryConfig = NonNullable<ReturnType<typeof resolveSentryConfig>>;
type WebSentryBeforeSendEvent = {
  readonly title?: unknown;
  readonly exception?: unknown;
  request?: { url?: string };
  extra?: Record<string, unknown>;
  tags?: Record<string, unknown>;
};

const FEEDBACK_INTEGRATION_OPTIONS = {
  autoInject: false,
  colorScheme: "system",
  formTitle: "Report a Bug",
  themeLight: {
    accentBackground: "oklch(0.52 0.19 48)",
    accentForeground: "#ffffff",
    background: "#ffffff",
    boxShadow: "0 24px 80px rgba(0, 0, 0, 0.18)",
    foreground: "#262626",
    outline: "1px solid oklch(0.52 0.19 48)",
  },
  themeDark: {
    accentBackground: "oklch(0.62 0.2 47)",
    accentForeground: "#ffffff",
    background: "#171717",
    boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
    foreground: "#f5f5f5",
    outline: "1px solid oklch(0.62 0.2 47)",
  },
} as const;

function getSentryEventText(event: { readonly title?: unknown; readonly exception?: unknown }) {
  const values =
    event.exception &&
    typeof event.exception === "object" &&
    "values" in event.exception &&
    Array.isArray(event.exception.values)
      ? event.exception.values
      : [];
  return [
    typeof event.title === "string" ? event.title : undefined,
    ...values.flatMap((value) => {
      if (value === null || typeof value !== "object") return [];
      const record = value as { readonly type?: unknown; readonly value?: unknown };
      return [
        typeof record.type === "string" ? record.type : undefined,
        typeof record.value === "string" ? record.value : undefined,
      ];
    }),
  ];
}

function hasInterruptedWsTransportRequest(event: { readonly exception?: unknown }): boolean {
  const values =
    event.exception &&
    typeof event.exception === "object" &&
    "values" in event.exception &&
    Array.isArray(event.exception.values)
      ? event.exception.values
      : [];

  return values.some((value) => {
    if (value === null || typeof value !== "object") return false;
    const record = value as {
      readonly value?: unknown;
      readonly stacktrace?: {
        readonly frames?: ReadonlyArray<{
          readonly filename?: unknown;
          readonly function?: unknown;
        }>;
      };
    };
    if (
      typeof record.value !== "string" ||
      !/All fibers interrupted without error/i.test(record.value)
    ) {
      return false;
    }

    return record.stacktrace?.frames?.some((frame) => {
      const filename = typeof frame.filename === "string" ? frame.filename : "";
      const functionName = typeof frame.function === "string" ? frame.function : "";
      return filename.includes("src/rpc/wsTransport.ts") && functionName === "WsTransport.request";
    });
  });
}

function shouldDropExpectedTransportEvent(event: {
  readonly title?: unknown;
  readonly exception?: unknown;
}): boolean {
  return (
    getSentryEventText(event).some(isTransportConnectionErrorMessage) ||
    hasInterruptedWsTransportRequest(event)
  );
}

interface AppSentryErrorBoundaryProps {
  readonly children: ReactNode;
}

interface AppSentryErrorBoundaryState {
  readonly hasError: boolean;
}

function navigateToNewChatFromErrorShell(): void {
  const url = new URL(window.location.href);
  if (isElectron) {
    url.hash = "/new";
  } else {
    url.pathname = "/new";
    url.search = "";
    url.hash = "";
  }
  window.location.assign(url.toString());
}

function resolveWebSentryConfig(): WebSentryConfig | null {
  const runtime = isElectron ? "electron-renderer" : "browser";
  const env = import.meta.env;
  const release =
    readFirstEnvValue(env, [...WEB_SENTRY_RELEASE_KEYS]) ??
    buildSentryRelease({
      appName: WEB_APP_NAME,
      version: APP_VERSION,
      commitHash: readFirstEnvValue(env, [...WEB_SENTRY_COMMIT_KEYS]),
    });
  return resolveSentryConfig({
    appName: WEB_APP_NAME,
    runtime,
    defaultEnvironment: readFirstEnvValue(env, [...WEB_SENTRY_ENV_KEYS]) ?? "development",
    defaultRelease: release,
    defaultEnabled: import.meta.env.PROD,
    defaultTracesSampleRate: 0,
    defaultReplaysSessionSampleRate: 0,
    defaultReplaysOnErrorSampleRate: 0,
    enabled: readFirstEnvValue(env, [...WEB_SENTRY_ENABLED_KEYS]),
    dsn: readFirstEnvValue(env, [...WEB_SENTRY_DSN_KEYS]),
    environment: readFirstEnvValue(env, [...WEB_SENTRY_ENV_KEYS]),
    release: readFirstEnvValue(env, [...WEB_SENTRY_RELEASE_KEYS]),
    debug: readFirstEnvValue(env, [...WEB_SENTRY_DEBUG_KEYS]),
    tracesSampleRate: readFirstEnvValue(env, [...WEB_SENTRY_TRACES_SAMPLE_RATE_KEYS]),
    profilesSampleRate: readFirstEnvValue(env, [...WEB_SENTRY_PROFILES_SAMPLE_RATE_KEYS]),
    replaysSessionSampleRate: readFirstEnvValue(env, [
      ...WEB_SENTRY_REPLAYS_SESSION_SAMPLE_RATE_KEYS,
    ]),
    replaysOnErrorSampleRate: readFirstEnvValue(env, [
      ...WEB_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE_KEYS,
    ]),
    tags: {
      platform: isElectron ? "desktop" : "web",
    },
  });
}

function filterWebSentryEvent<TEvent extends WebSentryBeforeSendEvent>(
  event: TEvent,
): TEvent | null {
  scrubSentryEvent(event);
  return shouldDropExpectedTransportEvent(event) ? null : event;
}

function buildReplaySampleOptions(config: WebSentryConfig) {
  return {
    ...(config.replaysOnErrorSampleRate !== undefined
      ? { replaysOnErrorSampleRate: config.replaysOnErrorSampleRate }
      : {}),
    ...(config.replaysSessionSampleRate !== undefined
      ? { replaysSessionSampleRate: config.replaysSessionSampleRate }
      : {}),
  };
}

function buildCommonSentryOptions(config: WebSentryConfig) {
  return {
    attachStacktrace: config.attachStacktrace,
    debug: config.debug,
    dsn: config.dsn,
    environment: config.environment,
    ignoreErrors: [...COMMON_SENTRY_IGNORE_ERRORS],
    initialScope: { tags: config.tags },
    maxBreadcrumbs: config.maxBreadcrumbs,
    normalizeDepth: config.normalizeDepth,
    sendDefaultPii: false,
    tracesSampleRate: config.tracesSampleRate,
    ...(config.release !== undefined ? { release: config.release } : {}),
    ...buildReplaySampleOptions(config),
  };
}

function initElectronWebSentry(config: WebSentryConfig): WebSentryModule | null {
  const integrations = [];
  if (config.tracesSampleRate > 0) {
    integrations.push(ElectronRendererSentry.browserTracingIntegration());
  }
  if ((config.replaysSessionSampleRate ?? 0) > 0 || (config.replaysOnErrorSampleRate ?? 0) > 0) {
    integrations.push(ElectronRendererSentry.replayIntegration());
  }
  const feedback = ElectronRendererSentry.feedbackIntegration(FEEDBACK_INTEGRATION_OPTIONS);
  feedbackIntegration = feedback;
  integrations.push(feedback);

  try {
    ElectronRendererSentry.init({
      ...buildCommonSentryOptions(config),
      beforeSend: (event) => filterWebSentryEvent(event),
      integrations,
    });
  } catch {
    return null;
  }
  initialized = true;
  return ElectronRendererSentry;
}

function initBrowserWebSentry(config: WebSentryConfig): WebSentryModule | null {
  const integrations = [];
  if (config.tracesSampleRate > 0) {
    integrations.push(BrowserSentry.browserTracingIntegration());
  }
  if ((config.replaysSessionSampleRate ?? 0) > 0 || (config.replaysOnErrorSampleRate ?? 0) > 0) {
    integrations.push(BrowserSentry.replayIntegration());
  }
  const feedback = BrowserSentry.feedbackIntegration(FEEDBACK_INTEGRATION_OPTIONS);
  feedbackIntegration = feedback;
  integrations.push(feedback);

  try {
    BrowserSentry.init({
      ...buildCommonSentryOptions(config),
      beforeSend: (event) => filterWebSentryEvent(event),
      integrations,
    });
  } catch {
    return null;
  }
  initialized = true;
  return BrowserSentry;
}

export function initWebSentry(): WebSentryModule | null {
  if (initialized) {
    return isElectron ? ElectronRendererSentry : BrowserSentry;
  }

  const config = resolveWebSentryConfig();
  if (!config) {
    return null;
  }

  return isElectron ? initElectronWebSentry(config) : initBrowserWebSentry(config);
}

export function attachWebFeedbackTrigger(element: Element): (() => void) | null {
  const sentry = initWebSentry();
  if (!sentry || typeof feedbackIntegration?.attachTo !== "function") {
    return null;
  }
  return feedbackIntegration.attachTo(element) ?? null;
}

export async function openWebFeedbackDialog(): Promise<boolean> {
  const sentry = initWebSentry();
  if (!sentry || typeof feedbackIntegration?.createForm !== "function") {
    return false;
  }

  try {
    feedbackDialog ??= await feedbackIntegration.createForm();
    if (!feedbackDialogAttached) {
      feedbackDialog.appendToDom();
      feedbackDialogAttached = true;
    }
    feedbackDialog.open();
    return true;
  } catch {
    return false;
  }
}

export function setWebSentryUser(
  user: { id: string; email?: string; username?: string } | null,
): void {
  const sentry = initWebSentry();
  if (!sentry) return;
  if (isElectron) {
    ElectronRendererSentry.setUser(user);
    return;
  }
  BrowserSentry.setUser(user);
}

interface CaptureWebErrorOptions {
  readonly showReportDialog?: boolean;
}

export function captureWebError(
  error: unknown,
  context?: Record<string, string>,
  options?: CaptureWebErrorOptions,
): void {
  const sentry = initWebSentry();
  if (!sentry) {
    return;
  }

  const normalizedError = toError(error);

  if (isElectron) {
    ElectronRendererSentry.withScope((scope) => {
      if (context) {
        scope.setTags(context);
      }
      const eventId = ElectronRendererSentry.captureException(normalizedError);
      if (options?.showReportDialog) {
        ElectronRendererSentry.showReportDialog({ eventId });
      }
    });
    return;
  }

  BrowserSentry.withScope((scope) => {
    if (context) {
      scope.setTags(context);
    }
    const eventId = BrowserSentry.captureException(normalizedError);
    if (options?.showReportDialog) {
      BrowserSentry.showReportDialog({ eventId });
    }
  });
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return new Error(error);
  }

  try {
    return new Error(JSON.stringify(error));
  } catch {
    return new Error("Unknown non-Error exception");
  }
}

export class AppSentryErrorBoundary extends Component<
  AppSentryErrorBoundaryProps,
  AppSentryErrorBoundaryState
> {
  constructor(props: AppSentryErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): AppSentryErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error): void {
    captureWebError(error, { origin: "react-boundary" }, { showReportDialog: true });
  }

  override render() {
    if (this.state.hasError) {
      return <AppErrorBoundaryFallback onGoToNewChat={navigateToNewChatFromErrorShell} />;
    }

    return this.props.children;
  }
}
